import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { catalogApi } from '@/api';
import { Alert, Button, Checkbox, Input, Select } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { companyRegisterSchema } from '@/validations';

import { AuthCard } from './AuthCard';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];

export function RegisterCompanyPage() {
  const { registerCompany } = useAuth();
  const navigate = useNavigate();

  const { data: industries } = useQuery({
    queryKey: ['industries'],
    queryFn: catalogApi.listIndustries,
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(companyRegisterSchema),
    defaultValues: { accepted_terms: false },
  });

  async function onSubmit(values) {
    try {
      const session = await registerCompany({
        ...values,
        industry_id: values.industry_id || null,
        website: values.website || null,
        company_size: values.company_size || null,
        location: values.location || null,
      });
      navigate(`${paths.verifyEmail}?email=${encodeURIComponent(session.user.email)}`, {
        state: { debugCode: session.verification_code_debug ?? null, codeSent: true },
      });
    } catch (error) {
      if (!applyFieldErrors(error, setError)) {
        setError('root', { message: errorMessage(error, 'Could not create the account') });
      }
    }
  }

  return (
    <AuthCard
      title="Create your company account"
      description="Publish internships and manage candidates with your team."
      accountSwitch
      heading="Hire your next intern from people ready to start."
      points={[
        'Publish internships students actually find',
        'Candidates ranked by how well they match',
        'Interviews, offers and your whole team in one place',
      ]}
      footer={
        <>
          Looking for an internship?{' '}
          <Link to={paths.registerStudent} className="text-brand-600 font-medium">
            Create a student account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {errors.root && <Alert tone="error">{errors.root.message}</Alert>}

        <p className="text-ink-muted flex items-center gap-2 pt-1 text-[11px] font-semibold tracking-wide uppercase">
          About you
          <span aria-hidden="true" className="bg-line h-px flex-1" />
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Your name"
            autoComplete="name"
            required
            error={errors.full_name?.message}
            {...register('full_name')}
          />
          <Input
            label="Work email"
            type="email"
            autoComplete="email"
            required
            error={errors.email?.message}
            {...register('email')}
          />
        </div>
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          hint="At least 8 characters, with upper and lower case and a number."
          error={errors.password?.message}
          {...register('password')}
        />

        <p className="text-ink-muted flex items-center gap-2 pt-1 text-[11px] font-semibold tracking-wide uppercase">
          Your company
          <span aria-hidden="true" className="bg-line h-px flex-1" />
        </p>

        <Input
          label="Company name"
          required
          error={errors.company_name?.message}
          {...register('company_name')}
        />

        <p className="text-ink-muted text-xs">
          The details below are optional — you can add them later from your company profile.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Industry"
            optional
            error={errors.industry_id?.message}
            {...register('industry_id')}
          >
            <option value="">Select later</option>
            {industries?.map((industry) => (
              <option key={industry.id} value={industry.id}>
                {industry.name}
              </option>
            ))}
          </Select>
          <Select label="Company size" optional {...register('company_size')}>
            <option value="">Select later</option>
            {COMPANY_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} employees
              </option>
            ))}
          </Select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Location"
            optional
            error={errors.location?.message}
            {...register('location')}
          />
          <Input
            label="Website"
            optional
            placeholder="https://"
            error={errors.website?.message}
            {...register('website')}
          />
        </div>

        <Checkbox
          label={
            <>
              I accept the <span className="underline">terms</span> and{' '}
              <span className="underline">privacy policy</span>
            </>
          }
          error={errors.accepted_terms?.message}
          {...register('accepted_terms')}
        />

        <Button type="submit" fullWidth isLoading={isSubmitting}>
          Create company account
        </Button>
      </form>
    </AuthCard>
  );
}
